"use client"

import { useState, useEffect, useRef } from "react"
import {
  Card, CardContent, CardMedia, Typography, Grid, Box, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, TextField, CircularProgress, InputAdornment,
} from "@mui/material"
import Close from "@mui/icons-material/Close"
import EditIcon from "@mui/icons-material/Edit"
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera"
import { Link } from "react-router-dom"
import { collection, getDocs, doc, setDoc, updateDoc, addDoc, deleteDoc } from "firebase/firestore"
import { ref, uploadBytes, getDownloadURL } from "firebase/storage"
import { db, storage } from "../config/firebase"
import { useAuth } from "../AuthContext"

import massageImg from "../assets/images/wellness-images/massageback.jpg"
import headSpaImg from "../assets/images/wellness-images/headspa.png"
import facialsImg from "../assets/images/wellness-images/facialsImg.jpg"
import bodyTreatmentsImg from "../assets/images/wellness-images/blackrockonback.jpg"
import scrubsImg from "../assets/images/wellness-images/scrub.webp"
import expressImg from "../assets/images/wellness-images/expressImg.jpg"
import bathSaunaImg from "../assets/images/wellness-images/lemonsonwater.jpg"
import agapepoolImg from "../assets/images/wellness-images/agapepool.jpg"
import Footer from "./Footer"
import PageHeader from "./PageHeader"

const IMAGE_MAP = {
  massageImg,
  headSpaImg,
  facialsImg,
  bodyTreatmentsImg,
  scrubsImg,
  expressImg,
  bathSaunaImg,
  agapepoolImg,
}

const DEFAULT_WELLNESS = [
  { id: "wellness-1", name: "Massages",           subtitle: "Full Body, Swedish, Deep Tissue",  imageKey: "massageImg",        description: "Relax and rejuvenate with our expert massage therapies.",                                                                               price: "$150" },
  { id: "wellness-2", name: "Head Spa",            subtitle: "Scalp Therapy",                    imageKey: "headSpaImg",        description: "Restore your mind with our luxury head spa treatments.",                                                                                price: "$150" },
  { id: "wellness-3", name: "Facials",             subtitle: "Custom Skin Rejuvenation",         imageKey: "facialsImg",        description: "Glow with our facial treatments tailored for your skin.",                                                                               price: "$150" },
  { id: "wellness-4", name: "Body Treatments",     subtitle: "Exfoliation & Detox",              imageKey: "bodyTreatmentsImg", description: "Indulge your body with our detox and exfoliation services.",                                                                             price: "$150" },
  { id: "wellness-5", name: "Scrubs",              subtitle: "Custom Scrub Packages",            imageKey: "scrubsImg",         description: "Exfoliate and nourish with our luxury body scrubs.",                                                                                    price: "$150" },
  { id: "wellness-6", name: "Express Treatments",  subtitle: "Quick Luxury",                     imageKey: "expressImg",        description: "Short on time? Recharge fast with our express services.",                                                                               price: "$150" },
  { id: "wellness-7", name: "Bath/Sauna Rental",   subtitle: "Sauna, Jacuzzi, Arctic Tub",       imageKey: "bathSaunaImg",      description: "Full private spa experiences with all the amenities.",                                                                                  price: "$150" },
  { id: "wellness-8", name: "Agape Pool Experience", subtitle: "Private Pool & Wellness",        imageKey: "agapepoolImg",      description: "Relax and rejuvenate at our luxurious pool with exclusive wellness services, refreshments, and stunning views.",                         price: "$150" },
]

const CARD_WIDTH = 320
const CARD_HEIGHT = 390
const IMAGE_HEIGHT = 170
const modernDisplayFont = "Satoshi, Inter, SF Pro Display, system-ui, -apple-system, sans-serif"
const modernBodyFont = "Inter, SF Pro Text, system-ui, -apple-system, sans-serif"

const resolveImage = (service) => {
  if (service.imageUrl) return service.imageUrl
  if (service.imageKey && IMAGE_MAP[service.imageKey]) return IMAGE_MAP[service.imageKey]
  return massageImg
}

// Strip everything but digits/decimal point, then re-add a single leading $ — handles legacy "$150" data too
const sanitizePrice = (raw) => {
  let value = String(raw || "").replace(/[^0-9.]/g, "")
  const firstDot = value.indexOf(".")
  if (firstDot !== -1) {
    value = value.slice(0, firstDot + 1) + value.slice(firstDot + 1).replace(/\./g, "")
  }
  const [whole, decimal] = value.split(".")
  return decimal !== undefined ? `${whole}.${decimal.slice(0, 2)}` : value
}

const formatPrice = (raw) => {
  const cleaned = sanitizePrice(raw)
  return cleaned ? `$${cleaned}` : "—"
}

const WellnessCard = () => {
  const { isAdmin } = useAuth()
  const [services, setServices]   = useState([])
  const [loading, setLoading]     = useState(true)

  // Edit state
  const [editService, setEditService]   = useState(null)
  const [editForm, setEditForm]         = useState({})
  const [saving, setSaving]             = useState(false)
  const [savedMsg, setSavedMsg]         = useState(false)
  const [uploadingImg, setUploadingImg] = useState(false)
  const [previewUrl, setPreviewUrl]     = useState(null)
  const fileInputRef = useRef(null)

  // ── Load / seed ──────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDocs(collection(db, "wellnessServices"))
        if (snap.empty) {
          const seeded = DEFAULT_WELLNESS.map((s) => ({ ...s, available: true }))
          await Promise.all(seeded.map((s) => setDoc(doc(db, "wellnessServices", s.id), s)))
          setServices(seeded)
        } else {
          const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
          data.sort((a, b) => a.id.localeCompare(b.id))
          setServices(data)
        }
      } catch (err) {
        console.error(err)
        setServices(DEFAULT_WELLNESS)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  // ── Edit open / close ────────────────────────────────────────────────
  const handleEditOpen = (e, service) => {
    if (e) e.stopPropagation()
    setEditService(service)
    setPreviewUrl(resolveImage(service))
    setEditForm({
      name:        service.name        || "",
      subtitle:    service.subtitle    || "",
      description: service.description || "",
      price:       sanitizePrice(service.price),
    })
    setSavedMsg(false)
  }

  const handleEditClose = () => {
    setEditService(null)
    setEditForm({})
    setPreviewUrl(null)
    setSavedMsg(false)
  }

  // ── Image upload ─────────────────────────────────────────────────────
  const handleImageChange = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    setPreviewUrl(URL.createObjectURL(file))
    setUploadingImg(true)

    try {
      const storageRef = ref(storage, `wellness-images/${editService.id}-${Date.now()}`)
      await uploadBytes(storageRef, file)
      const downloadUrl = await getDownloadURL(storageRef)

      await updateDoc(doc(db, "wellnessServices", editService.id), { imageUrl: downloadUrl })

      setServices((prev) =>
        prev.map((s) => s.id === editService.id ? { ...s, imageUrl: downloadUrl } : s)
      )
      setEditService((prev) => ({ ...prev, imageUrl: downloadUrl }))
      setPreviewUrl(downloadUrl)
    } catch (err) {
      console.error("Image upload error:", err)
    } finally {
      setUploadingImg(false)
    }
  }

  // ── Add new / Delete ─────────────────────────────────────────────────
  const handleAddNew = async () => {
    const newService = {
      name: "New Wellness Experience",
      subtitle: "Add a service type",
      description: "Describe this service.",
      price: "0",
      available: true,
    }
    try {
      const ref = await addDoc(collection(db, "wellnessServices"), newService)
      const created = { id: ref.id, ...newService }
      setServices((prev) => [...prev, created])
      handleEditOpen(null, created)
    } catch (err) {
      console.error("Add service error:", err)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm("Delete this service? This cannot be undone.")) return
    setSaving(true)
    try {
      await deleteDoc(doc(db, "wellnessServices", editService.id))
      setServices((prev) => prev.filter((s) => s.id !== editService.id))
      handleEditClose()
    } catch (err) {
      console.error("Delete service error:", err)
    } finally {
      setSaving(false)
    }
  }

  // ── Availability toggle ───────────────────────────────────────────────
  const handleToggleAvailability = async (e, service) => {
    e.stopPropagation()
    const newAvailable = service.available === false
    try {
      await updateDoc(doc(db, "wellnessServices", service.id), { available: newAvailable })
      setServices((prev) =>
        prev.map((s) => s.id === service.id ? { ...s, available: newAvailable } : s)
      )
      if (editService?.id === service.id) {
        setEditService((prev) => ({ ...prev, available: newAvailable }))
      }
    } catch (err) {
      console.error("Availability toggle error:", err)
    }
  }

  // ── Save text fields ─────────────────────────────────────────────────
  const handleEditSave = async () => {
    setSaving(true)
    try {
      const updated = {
        name:        editForm.name,
        subtitle:    editForm.subtitle,
        description: editForm.description,
        price:       editForm.price,
      }

      await updateDoc(doc(db, "wellnessServices", editService.id), updated)
      setServices((prev) =>
        prev.map((s) => s.id === editService.id ? { ...s, ...updated } : s)
      )
      setSavedMsg(true)
      setTimeout(handleEditClose, 1200)
    } catch (err) {
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <Typography sx={{ color: "white", fontFamily: modernBodyFont }}>Loading...</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ position: "relative", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Background video */}
      <video autoPlay loop muted playsInline
        style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover", zIndex: -1 }}>
        <source src="/videos/sunsetbluedark.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Foreground content */}
      <Box sx={{ width: "100%", minHeight: "100vh", display: "flex", flexDirection: "column", position: "relative", zIndex: 1, flex: 1 }}>

        {/* MAIN CONTENT */}
        <Box sx={{ px: { xs: 2, md: 0 }, py: 4, paddingTop: 3, width: "100%", maxWidth: 1360, margin: "0 auto", fontFamily: modernBodyFont, flex: 1 }}>

          <PageHeader
            docId="wellness-header"
            defaultTitle="Wellness Center"
            defaultDescription="Immerse yourself in tranquility with our curated wellness experiences designed to restore balance and rejuvenate your spirit."
            sx={{ mb: 0 }}
            descSx={{ letterSpacing: "-0.01em", mb: 4, color: "#000000ff" }}
          />

          <Grid container spacing={3} justifyContent="center" alignItems="stretch" sx={{ fontFamily: modernBodyFont }}>
            {services.map((service) => (
              <Grid item key={service.id} xs={12} sm={6} md={3} display="flex" flexDirection="column" alignItems="center">

                {/* Wrapper for pencil icon positioning */}
                <Box sx={{ position: "relative", width: `${CARD_WIDTH}px` }}>

                  {/* Pencil icon — admin only */}
                  {isAdmin && (
                    <IconButton onClick={(e) => handleEditOpen(e, service)} size="small"
                      sx={{ position: "absolute", top: 8, left: 8, zIndex: 10, backgroundColor: "rgba(255,255,255,0.92)", width: 30, height: 30, boxShadow: "0 2px 8px rgba(0,0,0,0.3)", "&:hover": { backgroundColor: "#fff", transform: "scale(1.1)" } }}>
                      <EditIcon sx={{ fontSize: 15, color: "#0d1b4c" }} />
                    </IconButton>
                  )}

                  {/* Availability badge — visible to all; admins can click to toggle */}
                  <Box
                    onClick={isAdmin ? (e) => handleToggleAvailability(e, service) : undefined}
                    sx={{ position: "absolute", top: 8, left: isAdmin ? 46 : 8, zIndex: 10, cursor: isAdmin ? "pointer" : "default", backgroundColor: service.available !== false ? "rgba(22,163,74,0.9)" : "rgba(185,28,28,0.85)", color: "#fff", px: 1, py: 0.25, borderRadius: "12px", fontSize: "0.65rem", fontWeight: 700, lineHeight: "20px", height: 20, display: "flex", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.3)", userSelect: "none", ...(isAdmin && { "&:hover": { filter: "brightness(1.1)" } }) }}>
                    {service.available !== false ? "Available" : "Unavailable"}
                  </Box>

                  <Card sx={{ width: `${CARD_WIDTH}px`, height: `${CARD_HEIGHT}px`, display: "flex", flexDirection: "column", borderRadius: 4, opacity: service.available !== false ? 1 : 0.7, backgroundColor: "rgba(255,255,255,0.08)", backdropFilter: "blur(24px)", border: service.available !== false ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(185,28,28,0.4)", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)", fontFamily: modernBodyFont, transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)", "&:hover": { transform: "translateY(-8px) scale(1.02)", backgroundColor: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", boxShadow: "0 40px 80px -12px rgba(0,0,0,0.4)" } }}>
                    <Box sx={{ position: "relative" }}>
                      <CardMedia component="img" image={resolveImage(service)} alt={service.name}
                        sx={{ height: IMAGE_HEIGHT, objectFit: "cover", borderTopLeftRadius: 16, borderTopRightRadius: 16 }} />
                      <Box sx={{ fontFamily: modernBodyFont, fontWeight: 500, letterSpacing: "-0.01em", position: "absolute", top: 8, right: 8, backgroundColor: "rgba(15,23,42,0.9)", backdropFilter: "blur(4px)", color: "#fff", px: 1.5, py: 0.5, borderRadius: "16px", fontSize: "0.9rem" }}>
                        {formatPrice(service.price)}
                        <span style={{ fontWeight: 300, color: "rgba(203,213,225,1)" }}>/session</span>
                      </Box>
                    </Box>

                    <CardContent sx={{ flexGrow: 1, pb: 1, fontFamily: modernBodyFont }}>
                      <Typography variant="h6" align="center" fontWeight={600}
                        sx={{ fontFamily: modernDisplayFont, color: "#ffffff", textShadow: "0 2px 8px rgba(0,0,0,0.3)", letterSpacing: "-0.01em" }}>
                        {service.name}
                      </Typography>
                      <Typography variant="subtitle2" align="center" fontWeight={400}
                        sx={{ fontFamily: modernBodyFont, color: "rgba(255,255,255,0.8)", textShadow: "0 1px 4px rgba(0,0,0,0.2)", letterSpacing: "0.01em" }}>
                        {service.subtitle}
                      </Typography>
                      <Typography variant="body2" mt={1} align="center"
                        sx={{ fontFamily: modernBodyFont, color: "rgba(255,255,255,0.9)", textShadow: "0 1px 4px rgba(0,0,0,0.2)", lineHeight: 1.5 }}>
                        {service.description}
                      </Typography>
                    </CardContent>

                    <Box sx={{ p: 2, pt: 0 }}>
                      <Button component={Link} to="/reserve-wellness" variant="contained" fullWidth
                        sx={{ borderRadius: 3, fontWeight: 600, fontSize: "0.95rem", backgroundColor: "rgba(15,23,42,0.9)", backdropFilter: "blur(4px)", border: "none", color: "#ffffff", fontFamily: modernBodyFont, textShadow: "0 1px 2px rgba(0,0,0,0.2)", letterSpacing: "0.02em", py: 1.5, transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)", "&:hover": { backgroundColor: "rgba(15,23,42,1)", transform: "translateY(-2px)", boxShadow: "0 8px 25px rgba(15,23,42,0.4)" } }}>
                        BOOK NOW
                      </Button>
                    </Box>
                  </Card>

                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Add New button — admin only */}
          {isAdmin && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Button
                variant="outlined"
                onClick={handleAddNew}
                sx={{ color: "white", borderColor: "rgba(255,255,255,0.4)", borderRadius: "10px", px: 3, py: 1.25, fontSize: "0.9rem", fontWeight: 600, letterSpacing: "0.02em", backdropFilter: "blur(8px)", backgroundColor: "rgba(255,255,255,0.07)", "&:hover": { borderColor: "white", backgroundColor: "rgba(255,255,255,0.14)" } }}
              >
                + Add Wellness Service
              </Button>
            </Box>
          )}
        </Box>

        {/* Admin Edit Dialog */}
        <Dialog open={Boolean(editService)} onClose={handleEditClose} maxWidth="sm" fullWidth
          PaperProps={{ sx: { backgroundColor: "#0d1b4c", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "16px" } }}>
          {editService && (
            <>
              <DialogTitle sx={{ fontFamily: modernDisplayFont, fontWeight: 600, color: "white", display: "flex", alignItems: "center", gap: 1 }}>
                <EditIcon sx={{ fontSize: 18 }} /> Edit — {editService.name}
                <IconButton onClick={handleEditClose} sx={{ position: "absolute", right: 8, top: 8, color: "white" }}><Close /></IconButton>
              </DialogTitle>

              <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>

                {/* Image upload section */}
                <Box>
                  <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: 12, letterSpacing: 1, textTransform: "uppercase", mb: 1 }}>
                    Service Photo
                  </Typography>
                  <Box sx={{ position: "relative", borderRadius: "10px", overflow: "hidden", mb: 1 }}>
                    <img src={previewUrl} alt="preview" style={{ width: "100%", height: 160, objectFit: "cover", display: "block", borderRadius: 10 }} />
                    {uploadingImg && (
                      <Box sx={{ position: "absolute", inset: 0, backgroundColor: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "10px" }}>
                        <CircularProgress size={32} sx={{ color: "white" }} />
                      </Box>
                    )}
                  </Box>
                  <input type="file" accept="image/*" ref={fileInputRef} onChange={handleImageChange} style={{ display: "none" }} />
                  <Button variant="outlined" size="small" startIcon={<PhotoCameraIcon />}
                    onClick={() => fileInputRef.current.click()} disabled={uploadingImg}
                    sx={{ color: "rgba(255,255,255,0.7)", borderColor: "rgba(255,255,255,0.25)", borderRadius: "8px", fontSize: 12, "&:hover": { borderColor: "rgba(255,255,255,0.6)", backgroundColor: "rgba(255,255,255,0.05)" } }}>
                    {uploadingImg ? "Uploading..." : "Change Photo"}
                  </Button>
                </Box>

                {/* Text fields */}
                {[
                  { label: "Name",         field: "name",     helperText: "Full display name shown on the card — e.g. \"Massages\"" },
                  { label: "Service Type", field: "subtitle", helperText: "Short label under the name — e.g. \"Full Body, Swedish, Deep Tissue\"" },
                ].map(({ label, field, helperText }) => (
                  <TextField key={field} label={label} value={editForm[field] || ""} onChange={(e) => setEditForm((p) => ({ ...p, [field]: e.target.value }))} fullWidth size="small"
                    helperText={helperText}
                    InputLabelProps={{ style: { color: "rgba(255,255,255,0.6)" } }}
                    InputProps={{ style: { color: "white" } }}
                    FormHelperTextProps={{ style: { color: "rgba(255,255,255,0.45)", fontSize: "0.72rem" } }}
                    sx={{ "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "rgba(255,255,255,0.2)" }, "&:hover fieldset": { borderColor: "rgba(255,255,255,0.5)" }, "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.8)" } } }}
                  />
                ))}

                {/* Price — $ prefix locked in, digits only */}
                <TextField label="Price" value={editForm.price || ""}
                  onChange={(e) => setEditForm((p) => ({ ...p, price: sanitizePrice(e.target.value) }))}
                  fullWidth size="small"
                  helperText="Numbers only (decimals OK, e.g. 149.99) — the $ is added automatically. Shown as $XXX/session on the card."
                  InputLabelProps={{ style: { color: "rgba(255,255,255,0.6)" } }}
                  InputProps={{
                    style: { color: "white" },
                    startAdornment: <InputAdornment position="start" sx={{ "& .MuiTypography-root": { color: "rgba(255,255,255,0.8)", fontWeight: 600 } }}>$</InputAdornment>,
                    inputMode: "numeric",
                  }}
                  FormHelperTextProps={{ style: { color: "rgba(255,255,255,0.45)", fontSize: "0.72rem" } }}
                  sx={{ "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "rgba(255,255,255,0.2)" }, "&:hover fieldset": { borderColor: "rgba(255,255,255,0.5)" }, "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.8)" } } }}
                />

                <TextField label="Description" value={editForm.description || ""} onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))} fullWidth multiline rows={3}
                  helperText='Short paragraph shown on the card — e.g. "Relax and rejuvenate with our expert massage therapies."'
                  InputLabelProps={{ style: { color: "rgba(255,255,255,0.6)" } }}
                  InputProps={{ style: { color: "white", fontSize: "0.9rem" } }}
                  FormHelperTextProps={{ style: { color: "rgba(255,255,255,0.45)", fontSize: "0.72rem" } }}
                  sx={{ "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "rgba(255,255,255,0.2)" }, "&:hover fieldset": { borderColor: "rgba(255,255,255,0.5)" }, "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.8)" } } }}
                />

                {/* Availability toggle */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 1.5, borderRadius: "8px", border: "1px solid rgba(255,255,255,0.15)", backgroundColor: "rgba(255,255,255,0.04)" }}>
                  <Box>
                    <Typography sx={{ color: "white", fontSize: 14, fontWeight: 500 }}>Availability</Typography>
                    <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.72rem", mt: 0.25 }}>
                      {editService.available !== false ? "Accepting bookings — visible in booking form" : "Bookings paused — hidden from booking form"}
                    </Typography>
                  </Box>
                  <Box onClick={(e) => handleToggleAvailability(e, editService)}
                    sx={{ cursor: "pointer", backgroundColor: editService.available !== false ? "rgba(22,163,74,0.9)" : "rgba(185,28,28,0.85)", color: "#fff", px: 2, py: 0.75, borderRadius: "8px", fontSize: "0.8rem", fontWeight: 700, userSelect: "none", minWidth: 104, textAlign: "center", flexShrink: 0, transition: "background 0.2s", "&:hover": { filter: "brightness(1.1)" } }}>
                    {editService.available !== false ? "Available" : "Unavailable"}
                  </Box>
                </Box>
              </DialogContent>

              <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
                <Button onClick={handleDelete} disabled={saving}
                  sx={{ color: "#f87171", border: "1px solid rgba(248,113,113,0.4)", borderRadius: "8px", "&:hover": { backgroundColor: "rgba(248,113,113,0.1)", borderColor: "#f87171" } }}>
                  Delete Service
                </Button>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button onClick={handleEditClose} sx={{ color: "rgba(255,255,255,0.6)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px" }}>Cancel</Button>
                  <Button variant="contained" onClick={handleEditSave} disabled={saving || uploadingImg}
                    sx={{ backgroundColor: savedMsg ? "#1a7a4a" : "white", color: savedMsg ? "white" : "#0d1b4c", fontWeight: 600, borderRadius: "8px", "&:hover": { backgroundColor: savedMsg ? "#1a7a4a" : "#f0f0f0" } }}>
                    {saving ? "Saving..." : savedMsg ? "✓ Saved!" : "Save Changes"}
                  </Button>
                </Box>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* FOOTER */}
        <Footer sx={{ marginTop: "auto", width: "100vw", left: "50%", marginLeft: "-50vw", marginRight: "-50vw", marginBottom: 0 }} />
      </Box>
    </Box>
  )
}

export default WellnessCard
